import { useState } from "react";

const n = (v) => parseFloat(v) || 0;
const fmtAmt = (v) =>
  (parseFloat(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const fmtWt = (v) =>
  (parseFloat(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });

// ── Shared style tokens ───────────────────────────────────────────────────────
const C = {
  border: "1px solid #c8960a",
  hdrBg: "#5c3f00",
  bodyBg: "#3a2600",
  colHdrBg: "#2a1a00",
  gold: "#ffb703",
  goldDim: "#c8960a",
  white: "#fff",
  muted: "#ccc",
  green: "#4caf50",
  amber: "#ffb703",
  red: "#ff6b6b",
};

const colLbl = {
  fontSize: 10,
  color: C.goldDim,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: ".3px",
  marginBottom: 1,
  whiteSpace: "nowrap",
};
const colVal = { fontSize: 12, fontWeight: 700, color: C.white };

// ── Helpers ───────────────────────────────────────────────────────────────────
function remColor(amount, paid) {
  const r = n(amount) - n(paid);
  return r <= 0 ? C.green : n(paid) > 0 ? C.amber : C.red;
}

function Badge({ ok, okText, warnText }) {
  return ok ? (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        background: "#1a3a1a",
        color: C.green,
        border: "1px solid #2d5c2d",
        borderRadius: 3,
        padding: "1px 6px",
        fontSize: 10,
      }}
    >
      ✓ {okText}
    </span>
  ) : (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        background: "#3a2200",
        color: C.amber,
        border: "1px solid #5c3800",
        borderRadius: 3,
        padding: "1px 6px",
        fontSize: 10,
      }}
    >
      ⚠ {warnText}
    </span>
  );
}

// ── Cell in header summary bar ────────────────────────────────────────────────
function SummaryCell({ label, value, color = C.white, minWidth }) {
  return (
    <div style={{ textAlign: "right", minWidth }}>
      <div style={colLbl}>{label}</div>
      <div style={{ ...colVal, color }}>{value}</div>
    </div>
  );
}

// ── Column header row inside accordion ───────────────────────────────────────
function ColHeader({ cols, gridCols }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "32px " + gridCols,
        background: C.colHdrBg,
        borderBottom: C.border,
      }}
    >
      <span style={{ ...colLbl, padding: "5px 8px", paddingLeft: 12 }}>#</span>
      {cols.map((c, i) => (
        <span
          key={i}
          style={{
            ...colLbl,
            padding: "5px 8px",
            textAlign: c.align || "right",
          }}
        >
          {c.header}
        </span>
      ))}
    </div>
  );
}

// ── One transaction row ───────────────────────────────────────────────────────
function TxRow({ index, cols, tx, paidAmount, gridCols }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "32px " + gridCols,
        alignItems: "center",
        borderBottom: "1px solid #2a1800",
      }}
    >
      <div
        style={{
          padding: "6px 8px 6px 12px",
          fontSize: 11,
          color: C.goldDim,
          fontWeight: 700,
        }}
      >
        #{index + 1}
      </div>
      {cols.map((col, i) => {
        const content = col.render(tx, paidAmount);
        return (
          <div
            key={i}
            style={{
              padding: "6px 8px",
              fontSize: 11,
              textAlign: col.align || "right",
              color: col.color
                ? col.color(tx, paidAmount)
                : i === 0
                  ? C.white
                  : C.muted,
            }}
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}

// ── SECTION CONFIGS — defines columns per section ────────────────────────────
// Each col: { header, align, render(tx, paidAmt), color?(tx, paidAmt) }
// In SECTION_CONFIGS, add a `width` to every col:
const SECTION_CONFIGS = {
  pc: {
    weightKey: "totalWeight",
    cols: [
      {
        header: "Brand",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => (t.brand && t.brand !== "Select" ? t.brand : "—"),
      },
      {
        header: "Qty",
        align: "right",
        width: "40px",
        render: (t) => t.qty || "—",
      },
      {
        header: "Wt/pc",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.weight)) + "g",
      },
      {
        header: "Total Wt",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.totalWeight)) + "g",
      },
      {
        header: "Diff",
        align: "right",
        width: "40px",
        render: (t) => t.diff || "—",
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
  lab: {
    weightKey: "weight",
    cols: [
      {
        header: "Weight",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => fmtWt(n(t.weight)) + "g",
      },
      {
        header: "Report",
        align: "right",
        width: "80px",
        render: (t) => (
          <Badge ok={t.report != null} okText={t.report} warnText="Pending" />
        ),
      },
      {
        header: "Pure Wt",
        align: "right",
        width: "90px",
        render: (t) => (
          <Badge
            ok={t.pureWeight != null}
            okText={fmtWt(n(t.pureWeight)) + "g"}
            warnText="N/A"
          />
        ),
      },
      {
        header: "Diff",
        align: "right",
        width: "40px",
        render: (t) => t.diff || "—",
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
  bar: {
    weightKey: "totalWeight",
    cols: [
      {
        header: "Brand",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => (t.brand && t.brand !== "add-new" ? t.brand : "—"),
      },
      {
        header: "Wt/bar",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.weight)) + "g",
      },
      {
        header: "Qty",
        align: "right",
        width: "40px",
        render: (t) => t.qty || "—",
      },
      {
        header: "Total Wt",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.totalWeight)) + "g",
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
  coin: {
    weightKey: "totalWeight",
    cols: [
      {
        header: "Brand",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => t.brand || "—",
      },
      {
        header: "Total Wt",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.totalWeight)) + "g",
      },
      {
        header: "Pure Wt",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.pureWeight)) + "g",
      },
      {
        header: "Karat",
        align: "right",
        width: "50px",
        render: (t) => t.karat || "—",
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
  ginni: {
    weightKey: "totalWeight",
    cols: [
      {
        header: "Total Wt",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => fmtWt(n(t.totalWeight)) + "g",
      },
      {
        header: "Report",
        align: "right",
        width: "80px",
        render: (t) => (
          <Badge ok={t.report != null} okText={t.report} warnText="Pending" />
        ),
      },
      {
        header: "Pure Wt",
        align: "right",
        width: "90px",
        render: (t) => (
          <Badge
            ok={t.pureWeight != null}
            okText={fmtWt(n(t.pureWeight)) + "g"}
            warnText="N/A"
          />
        ),
      },
      {
        header: "Refine",
        align: "right",
        width: "80px",
        render: (t) => "Rs " + fmtAmt(t.refineCharges || 0),
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
  silver: {
    weightKey: "totalWeight",
    cols: [
      {
        header: "Brand",
        align: "left",
        width: "minmax(60px,1fr)",
        render: (t) => t.brand || "—",
      },
      {
        header: "Total Wt",
        align: "right",
        width: "80px",
        render: (t) => fmtWt(n(t.totalWeight)) + "g",
      },
      {
        header: "Charges",
        align: "right",
        width: "90px",
        render: (t) => "Rs " + fmtAmt(t.totalAmount),
      },
      {
        header: "Paid",
        align: "right",
        width: "90px",
        render: (t, pa) => "Rs " + fmtAmt(n(pa[t.id])),
        color: () => C.green,
      },
      {
        header: "Remaining",
        align: "right",
        width: "90px",
        render: (t, pa) => {
          const r = n(t.totalAmount) - n(pa[t.id]);
          return r <= 0 ? "✓ Paid" : "Rs " + fmtAmt(r);
        },
        color: (t, pa) => remColor(t.totalAmount, pa[t.id]),
      },
    ],
  },
};

// ── SectionAccordion ──────────────────────────────────────────────────────────
export function SectionAccordion({
  label,
  sectionKey,
  transactions,
  paidAmounts,
}) {
  const [open, setOpen] = useState(false);
  if (!transactions?.length) return null;

  const config = SECTION_CONFIGS[sectionKey];
  if (!config) return null;

  const { weightKey, cols } = config;
  const pa = paidAmounts || {};

  const totalWeight = transactions.reduce((s, t) => s + n(t[weightKey]), 0);
  const totalAmount = transactions.reduce((s, t) => s + n(t.totalAmount), 0);
  const totalPaid = Object.values(pa).reduce((s, v) => s + n(v), 0);
  const totalRem = totalAmount - totalPaid;
  const rc = remColor(totalAmount, totalPaid);

  // build grid template: first col left-aligned (flexible), rest auto
  const gridCols = cols.map((c) => c.width).join(" ");

  return (
    <div
      style={{
        border: C.border,
        borderRadius: 6,
        overflow: "hidden",
        marginBottom: 8,
      }}
    >
      {/* ── Summary header (clickable) ── */}
      <div
        onClick={() => setOpen((p) => !p)}
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto auto auto auto auto",
          gap: 12,
          padding: "7px 12px",
          background: C.hdrBg,
          cursor: "pointer",
          alignItems: "center",
        }}
      >
        <span style={{ color: C.gold, fontWeight: 700, fontSize: 13 }}>
          {label}
          <span
            style={{
              color: C.white,
              fontWeight: 400,
              fontSize: 11,
              marginLeft: 6,
            }}
          >
            ({transactions.length})
          </span>
        </span>
        <SummaryCell label="Weight" value={fmtWt(totalWeight) + "g"} />
        <SummaryCell label="Charges" value={"Rs " + fmtAmt(totalAmount)} />
        <SummaryCell
          label="Paid"
          value={"Rs " + fmtAmt(totalPaid)}
          color={C.green}
        />
        <SummaryCell
          label={totalRem <= 0 ? "Status" : "Remaining"}
          value={totalRem <= 0 ? "✓ Paid" : "Rs " + fmtAmt(totalRem)}
          color={rc}
          minWidth={80}
        />
        <span
          style={{
            color: C.gold,
            fontSize: 13,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform .2s",
          }}
        >
          ▾
        </span>
      </div>

      {/* ── Expanded transaction table ── */}
      {open && (
        <div style={{ background: C.bodyBg, borderTop: C.border }}>
          <ColHeader cols={cols} gridCols={gridCols} />
          {transactions.map((t, i) => (
            <TxRow
              key={t.id || i}
              index={i}
              cols={cols}
              tx={t}
              paidAmount={pa}
              gridCols={gridCols}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── CashRow — flat row for cash (no accordion needed) ─────────────────────────
export function CashRow({ amount }) {
  if (!n(amount)) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        background: C.hdrBg,
        border: C.border,
        borderRadius: 6,
        marginBottom: 8,
      }}
    >
      <span style={{ color: C.gold, fontWeight: 700, fontSize: 13 }}>Cash</span>
      <span style={{ color: C.white, fontSize: 13, fontWeight: 700 }}>
        Rs {fmtAmt(amount)}
      </span>
    </div>
  );
}

// ── RateWorkRow — flat row for Gold/Silver Rate of Work ───────────────────────
export function RateWorkRow({ label, rw }) {
  if (!n(rw?.weight) && !n(rw?.totalAmount)) return null;
  const statusColor = rw.status === "Pending" ? C.amber : C.green;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "8px 12px",
        background: C.bodyBg,
        border: C.border,
        borderRadius: 6,
        marginBottom: 8,
      }}
    >
      <span style={{ color: C.gold, fontWeight: 700, fontSize: 12 }}>
        {label}
      </span>
      <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
        <div style={{ textAlign: "right" }}>
          <div style={colLbl}>Weight</div>
          <div style={colVal}>{fmtWt(n(rw.weight))}g</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={colLbl}>Amount</div>
          <div style={colVal}>Rs {fmtAmt(rw.totalAmount)}</div>
        </div>
        {rw.rate && (
          <div style={{ textAlign: "right" }}>
            <div style={colLbl}>Rate</div>
            <div style={colVal}>Rs {fmtAmt(rw.rate)}</div>
          </div>
        )}
        <div style={{ textAlign: "right" }}>
          <div style={colLbl}>Status</div>
          <div style={{ ...colVal, color: statusColor }}>
            {rw.status || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
